import { m, useScroll } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
// @mui
import { styled, alpha, useTheme } from '@mui/material/styles';
import { Button, Box, Link, Container, Typography, Stack, Grid } from '@mui/material';
// routes
import { PATH_DASHBOARD } from '../../../routes/paths.jsx';
// hooks
import useResponsive from '../../../hooks/minimal/useResponsive.jsx';
// utils
import { textGradient, bgGradient } from '../../../utils/minimal/cssStyles.js';
// config
import { HEADER } from '../../../config-global.jsx';
// components
import SvgColor from '../../../components/minimal/svg-color/index.jsx';
import Iconify from '../../../components/minimal/iconify/index.jsx';
import { MotionContainer, varFade } from '../../../components/minimal/animate/index.jsx';
import { useLocales } from "../../../locales/index.js";
import { useSettingsContext } from "../../../components/minimal/settings/index.jsx";
import { StyledRoot } from "../../common/style.jsx";
import { getMarketPlaceName, getMarketPlaceTermsOfServiceAndPp } from "../../../utils/market.js";



// ----------------------------------------------------------------------
const [PATH_TO_TOS, PATH_TO_PP] = getMarketPlaceTermsOfServiceAndPp();

const StyledDescription = styled('div')(({ theme }) => ({
  maxWidth: 480,
  margin: 'auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(15, 0),
  height: '100%',
}));

const StyledGradientText = styled(m.h1)(({ theme }) => ({
  ...textGradient(
    theme.direction === 'rtl' ? `350deg, ${theme.palette.primary.main} 100%, ${theme.palette.warning.main} 75%, ${theme.palette.primary.main} 50%, ${theme.palette.warning.main} 25%, ${theme.palette.primary.main} 0%` : `300deg, ${theme.palette.primary.main} 0%, ${theme.palette.warning.main} 25%, ${theme.palette.primary.main} 50%, ${theme.palette.warning.main} 75%, ${theme.palette.primary.main} 100%`
  ),
  backgroundSize: '400%',
  fontFamily: "'Barlow', sans-serif",
  fontSize: `${64 / 16}rem`,
  textAlign: 'center',
  lineHeight: 1,
  padding: 0,
  marginTop: 8,
  marginBottom: 24,
  letterSpacing: 8,
  [theme.breakpoints.up('md')]: {
    fontSize: `${96 / 16}rem`,
  },
}));

const StyledEllipseTop = styled('div')(({ theme }) => ({
  position: 'absolute',
  width: 480,
  height: 480,
  top: -80,
  right: -80,
  borderRadius: '50%',
  filter: 'blur(100px)',
  WebkitFilter: 'blur(100px)',
  backgroundColor: alpha(theme.palette.primary.darker, 0.12),
}));

const StyledEllipseBottom = styled('div')(({ theme }) => ({
  position: 'absolute',
  height: 400,
  bottom: -200,
  left: '10%',
  right: '10%',
  borderRadius: '50%',
  filter: 'blur(100px)',
  WebkitFilter: 'blur(100px)',
  backgroundColor: alpha(theme.palette.primary.darker, 0.08),
}));

// ----------------------------------------------------------------------

export default function HomeHero() {
  const isDesktop = useResponsive('up', 'md');
  // const { scrollYProgress } = useScroll();

  // const [hide, setHide] = useState(false);
  //
  // useEffect(
  //   () =>
  //     scrollYProgress.onChange((scrollHeight) => {
  //       if (scrollHeight > 0.8) {
  //         setHide(true);
  //       } else {
  //         setHide(false);
  //       }
  //     }),
  //   [scrollYProgress]
  // );
  //
  // if (hide) {
  //   return null;
  // }
  return (
    <>
      <StyledRoot>
        <Container component={MotionContainer} sx={{ height: 1 }}>
          <Grid container spacing={10} sx={{ height: 1 }}>
            <Grid item xs={12} md={6} sx={{ height: 1 }}>
              <Description />
            </Grid>

            {isDesktop && (
              <Grid item xs={12} md={6}>
                <Content />
              </Grid>
            )}
          </Grid>
        </Container>

        {/* this cause issues on mobile device - <StyledEllipseTop />*/}

        {/* this cause issues on mobile device - <StyledEllipseBottom />*/}
      </StyledRoot>

      <Box sx={{ height: { md: '100vh' } }} />
    </>
  );
}

// ----------------------------------------------------------------------
function PpTos() {

  const { translate } = useLocales();
  const isDesktop = useResponsive('up', 'md');

  return (<Box sx={{
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    gap: 1,
    mt: isDesktop ? 6 : 0,
    fontSize: 14,
    opacity: 0.6,
  }}>
    <Link href={PATH_TO_TOS} target="_blank" rel="noopener" underline="hover" >
      {translate('terms_of_service')}
    </Link>
    <Link href={PATH_TO_PP} target="_blank" rel="noopener" underline="hover">
      {translate('privacy_policy')}
    </Link>
  </Box>)
}

// ----------------------------------------------------------------------

function Description() {

  const { translate } = useLocales();
  const { themeDirection } = useSettingsContext();

  return (
    <Box>
      <StyledDescription>
        <m.div variants={varFade().in}>
          <Typography variant="h2" sx={{ textAlign: 'center', minHeight: '100px' }}>
            {translate(getMarketPlaceName() + '.home.title')}
          </Typography>
        </m.div>

        <m.div variants={varFade().in}>
          <StyledGradientText
            sx={{ fontSize: "48px" }}
            animate={{ backgroundPosition: '200% center' }}
            transition={{
              repeatType: 'reverse',
              ease: 'linear',
              duration: 20,
              repeat: Infinity,
            }}
          >
            {getMarketPlaceName()}
          </StyledGradientText>
        </m.div>

        <m.div variants={varFade().in}>
          <Typography variant="body2" sx={{ textAlign: 'center' }}>
            {translate(getMarketPlaceName() + '.home.subtitle')}
          </Typography>
        </m.div>

        <m.div variants={varFade().in}>
          <Stack spacing={1.5} direction={{ xs: 'column-reverse', sm: 'row' }} sx={{ my: 5 }}>
            <Stack alignItems="center" spacing={2}>
              <Button
                component={RouterLink}
                to={PATH_DASHBOARD.root}
                color="inherit"
                size="large"
                variant="contained"
                endIcon={themeDirection === 'rtl' ? <Iconify icon="eva:flash-fill" width={24} /> : null}
                startIcon={themeDirection === 'rtl' ? null : <Iconify icon="eva:flash-fill" width={24} />}
                sx={{
                  bgcolor: 'text.primary',
                  color: (theme) => (theme.palette.mode === 'light' ? 'common.white' : 'grey.800'),
                  '&:hover': {
                    bgcolor: 'text.primary',
                  },
                }}
              >
                {translate(getMarketPlaceName() + '.home.main_cta')}
                {/*Live Preview*/}
              </Button>

              {/*<Link*/}
              {/*  color="inherit"*/}
              {/*  variant="caption"*/}
              {/*  target="_blank"*/}
              {/*  rel="noopener"*/}
              {/*  href={PATH_FREE_VERSION}*/}
              {/*  sx={{ textDecoration: 'underline', display: 'inline-flex', alignItems: 'center' }}*/}
              {/*>*/}
              {/*  <Iconify icon="eva:external-link-fill" width={16} sx={{ mr: 0.5 }} />*/}
              {/*  Get Free Version*/}
              {/*</Link>*/}
            </Stack>

            {/*<Button*/}
            {/*  color="inherit"*/}
            {/*  size="large"*/}
            {/*  variant="outlined"*/}
            {/*  startIcon={<Iconify icon="eva:external-link-fill" width={24} />}*/}
            {/*  target="_blank"*/}
            {/*  rel="noopener"*/}
            {/*  href={PATH_FIGMA_PREVIEW}*/}
            {/*  sx={{ borderColor: 'text.primary' }}*/}
            {/*>*/}
            {/*  Design Preview*/}
            {/*</Button>*/}
          </Stack>
        </m.div>

        {/*<Stack spacing={3} sx={{ textAlign: 'center', opacity: 0.4 }}>*/}
        {/*  <m.div variants={varFade().in}>*/}
        {/*    <Typography variant="overline">Available For</Typography>*/}
        {/*  </m.div>*/}

        {/*  <Stack spacing={2} direction="row" justifyContent="center">*/}
        {/*    {['sketch', 'figma', 'js', 'ts', 'nextjs'].map((platform) => (*/}
        {/*      <m.div key={platform} variants={varFade().in}>*/}
        {/*        <SvgColor src={`/assets/icons/platforms/ic_${platform}.svg`} />*/}
        {/*      </m.div>*/}
        {/*    ))}*/}
        {/*  </Stack>*/}
        {/*</Stack>*/}
      </StyledDescription>
      <PpTos />
    </Box>
  );
}

// ----------------------------------------------------------------------

function Content() {
  const theme = useTheme();

  const isLight = theme.palette.mode === 'light';

  const transition = {
    repeatType: 'loop',
    ease: 'linear',
    duration: 60 * 4,
    repeat: Infinity,
  };

  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      sx={{
        height: 1,
        overflow: 'hidden',
        position: 'absolute',
        top: 0,
      }}
    >
      <Stack component={m.div} variants={varFade().in} sx={{ width: 344, position: 'relative' }}>
        <Box
          component={m.img}
          animate={{ y: ['0%', '100%'] }}
          transition={transition}
          alt={`hero_${isLight ? 'light' : 'dark'}_1`}
          // src={`/assets/images/home/hero_${isLight ? 'light' : 'dark'}_1.png`}
          src="/assets/images/home/hero_light_1.png"
          sx={{ position: 'absolute' }}
        />
        <Box
          component={m.img}
          animate={{ y: ['-100%', '0%'] }}
          transition={transition}
          alt={`hero_${isLight ? 'light' : 'dark'}_1`}
          // src={`/assets/images/home/hero_${isLight ? 'light' : 'dark'}_1.png`}
          src="/assets/images/home/hero_light_1.png"
          sx={{ position: 'absolute' }}
        />
      </Stack>

      <Stack
        component={m.div}
        variants={varFade().in}
        sx={{ width: 720, position: 'relative', ml: -2 }}
      >
        <Box
          component={m.img}
          animate={{ y: ['100%', '0%'] }}
          transition={transition}
          alt={`hero_${isLight ? 'light' : 'dark'}_2`}
          // src={`/assets/images/home/hero_${isLight ? 'light' : 'dark'}_2.png`}
          src="/assets/images/home/hero_light_2.png"
          sx={{ position: 'absolute' }}
        />
        <Box
          component={m.img}
          animate={{ y: ['0%', '-100%'] }}
          transition={transition}
          alt={`hero_${isLight ? 'light' : 'dark'}_2`}
          // src={`/assets/images/home/hero_${isLight ? 'light' : 'dark'}_2.png`}
          src="/assets/images/home/hero_light_2.png"
          sx={{ position: 'absolute' }}
        />
      </Stack>
    </Stack>
  );
}
